import { configureStore as rtkConfigureStore } from '@reduxjs/toolkit';
import createLogger from 'redux-logger';
import createRootReducer from '../reducers/index';
import type { AuthState } from '../reducers/auth';

const isDev = process.env.NODE_ENV !== 'production';

// Redux Toolkit store — replaces the old createStore + hand-wired
// applyMiddleware/compose setup (and the duplicated dev/prod store files).
// getDefaultMiddleware already includes redux-thunk; devTools wires the Redux
// DevTools browser extension in development.
//
// Dev-only Redux diagnostics, OFF by default. Both immutableCheck/serializableCheck
// (deep-scan the whole state every dispatch) and redux-logger (serializes the whole
// state every action) are very expensive on this app's large state — ~200ms+ per
// dispatch, i.e. seconds of lag while typing in the editors. They already did their
// job during M5 (immutableCheck caught the in-place users.sort() mutation).
//
// Flip DEBUG_REDUX to true to hunt a suspected state mutation / non-serializable
// value (or to watch the action log); keep it false for normal, fast dev. The
// `tree` slice is still Immutable (M5 deferred it), so it's excluded from both
// checks along with the actions that carry Immutable node/tree payloads.
const DEBUG_REDUX = false;
const TREE_IMMUTABLE_ACTIONS = [
  'SELECT_NODE', 'DESELECT_NODE', 'TOGGLE_NODE', 'SET_CUSTOM_PICTURE',
  'SET_TOGGLED', 'RECEIVE_TREE_SUCCESS', 'RECEIVE_TARGET_SUCCESS',
  'PARSE_TARGETS', 'FILTER_TREE',
];
// Only `auth` is preloaded — the bootstrap reads the session after the redirect
// callback and passes it in, replacing a module-load localStorage read.
interface PreloadedState {
  auth?: AuthState;
}

const configureStore = (preloadedState?: PreloadedState) => {
  const store = rtkConfigureStore({
    reducer: createRootReducer(),
    preloadedState,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: DEBUG_REDUX && {
          ignoredPaths: ['tree'],
          ignoredActions: TREE_IMMUTABLE_ACTIONS,
        },
        immutableCheck: DEBUG_REDUX && {
          ignoredPaths: ['tree'],
        },
      });
      return (isDev && DEBUG_REDUX) ? middleware.concat(createLogger()) : middleware;
    },
    devTools: isDev,
  });

  return store;
};

// Store-derived types. RootState picks up each createSlice's inferred state (even
// from the still-.js reducer files, thanks to allowJs); AppDispatch is thunk-aware.
export type AppStore = ReturnType<typeof configureStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

export default configureStore;
